import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * SCROLL LOCK HOOK
 * Manages body scroll lock state properly with guaranteed cleanup.
 * Uses data attribute to track active locks and avoid conflicts.
 */
function useScrollLock(isLocked) {
  const scrollYRef = React.useRef(0);
  
  React.useEffect(() => {
    if (isLocked) {
      // Capture scroll position before locking
      scrollYRef.current = window.scrollY;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[useScrollLock] LOCK - scrollY:', scrollYRef.current);
      }
      
      // Apply scroll lock
      document.body.dataset.dialogScrollLock = 'true';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }
    
    return () => {
      if (isLocked) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[useScrollLock] UNLOCK - restoring scrollY:', scrollYRef.current);
        }
        
        // Clear scroll lock - always clear to empty (never restore "original")
        delete document.body.dataset.dialogScrollLock;
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollYRef.current);
      }
    };
  }, [isLocked]);
}

/**
 * MODAL DEBUG OVERLAY
 * Shows runtime values for debugging iOS Safari positioning issues
 * Enable via REACT_APP_MODAL_DEBUG=true in .env
 */
const ModalDebugOverlay = ({ contentRef }) => {
  const [debugInfo, setDebugInfo] = React.useState({});
  
  React.useEffect(() => {
    const updateDebugInfo = () => {
      const doc = document.documentElement;
      const content = contentRef?.current;
      
      // Get computed styles if content exists
      let computedStyles = {};
      let boundingRect = {};
      if (content) {
        const styles = window.getComputedStyle(content);
        computedStyles = {
          maxHeight: styles.maxHeight,
          inlineMaxHeight: content.style.maxHeight || '(not set)',
        };
        const rect = content.getBoundingClientRect();
        boundingRect = {
          top: Math.round(rect.top),
          left: Math.round(rect.left),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
          bottom: Math.round(rect.bottom),
          right: Math.round(rect.right),
        };
      }
      
      setDebugInfo({
        // CSS Variables
        safeModalHeight: getComputedStyle(doc).getPropertyValue('--safe-modal-height') || '(not set)',
        
        // Visual Viewport
        visualViewportHeight: window.visualViewport?.height || 'N/A',
        
        // Window dimensions
        innerHeight: window.innerHeight,
        
        // Computed styles
        ...computedStyles,
        
        // Bounding rect
        rect: boundingRect,
        
        // User agent
        isIOS: /iPhone|iPad|iPod/.test(navigator.userAgent),
        isSafari: /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent),
      });
    };
    
    updateDebugInfo();
    
    // Update on viewport changes
    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', updateDebugInfo);
      viewport.addEventListener('scroll', updateDebugInfo);
    }
    window.addEventListener('resize', updateDebugInfo);
    
    // Update periodically to catch any delayed changes
    const interval = setInterval(updateDebugInfo, 500);
    
    return () => {
      if (viewport) {
        viewport.removeEventListener('resize', updateDebugInfo);
        viewport.removeEventListener('scroll', updateDebugInfo);
      }
      window.removeEventListener('resize', updateDebugInfo);
      clearInterval(interval);
    };
  }, [contentRef]);
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: '10px',
        left: '10px',
        right: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: '#0f0',
        fontSize: '9px',
        fontFamily: 'monospace',
        padding: '6px',
        borderRadius: '4px',
        zIndex: 99999,
        maxHeight: '140px',
        overflowY: 'auto',
        pointerEvents: 'none',
      }}
      data-testid="modal-debug-overlay"
    >
      <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#ff0' }}>MODAL DEBUG</div>
      <div>--safe-modal-height: <span style={{ color: '#0ff' }}>{debugInfo.safeModalHeight}</span></div>
      <div>visualViewport.height: <span style={{ color: '#0ff' }}>{debugInfo.visualViewportHeight}</span></div>
      <div>window.innerHeight: <span style={{ color: '#0ff' }}>{debugInfo.innerHeight}</span></div>
      <div style={{ marginTop: '4px', fontWeight: 'bold', color: '#ff0' }}>DialogContent:</div>
      <div>maxHeight (computed): <span style={{ color: '#0ff' }}>{debugInfo.maxHeight}</span></div>
      <div>style.maxHeight (inline): <span style={{ color: '#0ff' }}>{debugInfo.inlineMaxHeight || 'N/A'}</span></div>
      <div style={{ marginTop: '2px' }}>
        <span style={{ color: debugInfo.isIOS ? '#0f0' : '#f00' }}>iOS: {debugInfo.isIOS ? 'YES' : 'NO'}</span>
        {' | '}
        <span style={{ color: debugInfo.isSafari ? '#0f0' : '#f00' }}>Safari: {debugInfo.isSafari ? 'YES' : 'NO'}</span>
      </div>
      <div style={{ marginTop: '2px', color: '#ff0' }}>Rect: t:{debugInfo.rect?.top} b:{debugInfo.rect?.bottom} h:{debugInfo.rect?.height}</div>
    </div>
  );
};

const MODAL_DEBUG = process.env.REACT_APP_MODAL_DEBUG === 'true';

/**
 * Dialog - Custom wrapper around Radix Dialog.Root
 * Handles scroll lock at this level to ensure proper cleanup when dialog closes.
 * The scroll lock is tied to the `open` prop, not to DialogContent mount/unmount.
 */
const Dialog = ({ open, onOpenChange, children, ...props }) => {
  // Handle scroll lock based on open state (NOT on content mount)
  useScrollLock(open);
  
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} {...props}>
      {children}
    </DialogPrimitive.Root>
  );
};

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

/**
 * GLOBAL DIALOG IMPLEMENTATION - iOS Safari Compatible
 * 
 * This implementation uses a FLEXBOX CENTERING approach that is proven
 * to work on iOS Safari, Android, and Desktop.
 * 
 * KEY STRATEGY:
 * - Overlay is a flex container that centers the modal
 * - Modal has fixed width constraints and respects viewport height
 * - Uses CSS variable --safe-modal-height set by visualViewport listener
 * - Internal scroll handled by DialogBody
 */

/**
 * DialogOverlay - The backdrop AND centering container
 * Uses flex centering to position the modal (more reliable than inset-0 m-auto on iOS)
 */
const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Fixed fullscreen backdrop
      "fixed inset-0 z-50 bg-black/80",
      // FLEX CENTERING - more reliable on iOS Safari than inset-0 m-auto
      "flex items-center justify-center",
      // Safe area padding to keep modal away from notches
      "p-3",
      // Animation
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * DialogContent - The actual modal container
 * 
 * iOS Safari Fix Strategy:
 * - Uses INLINE STYLES computed from visualViewport for max-height
 * - This bypasses CSS variable and dvh unit issues on iOS Safari
 * - Falls back to CSS variables for browsers where JS isn't needed
 * - flex flex-col for header/body/footer layout
 * - overflow-hidden on container, scroll in DialogBody
 * - NOTE: Scroll lock is now handled at the Dialog (Root) level via useScrollLock hook
 */
const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const internalRef = React.useRef(null);
  const [modalMaxHeight, setModalMaxHeight] = React.useState(null);
  
  const combinedRef = React.useMemo(() => {
    return (node) => {
      internalRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };
  }, [ref]);

  // Compute max-height dynamically using visualViewport
  // This is more reliable than CSS variables on iOS Safari
  React.useEffect(() => {
    const updateMaxHeight = () => {
      // Use visualViewport for accurate height (especially on iOS with keyboard)
      const vh = window.visualViewport?.height || window.innerHeight;
      // Leave 24px margin total (12px top + 12px bottom)
      const maxH = Math.floor(vh - 24);
      setModalMaxHeight(maxH);
    };
    
    updateMaxHeight();
    
    // Listen for viewport changes
    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', updateMaxHeight);
      viewport.addEventListener('scroll', updateMaxHeight);
    }
    window.addEventListener('resize', updateMaxHeight);
    
    return () => {
      if (viewport) {
        viewport.removeEventListener('resize', updateMaxHeight);
        viewport.removeEventListener('scroll', updateMaxHeight);
      }
      window.removeEventListener('resize', updateMaxHeight);
    };
  }, []);

  return (
    <DialogPortal>
      <DialogOverlay>
        <DialogPrimitive.Content
          ref={combinedRef}
          className={cn(
            // Reset any inherited positioning
            "relative",
            
            // WIDTH - mobile fits viewport, desktop has max-width
            "w-full max-w-[calc(100vw-24px)] sm:max-w-lg",
            
            // LAYOUT - flex column for header/body/footer structure
            "flex flex-col",
            
            // Container should NOT scroll - DialogBody handles scrolling
            "overflow-hidden",
            
            // VISUAL
            "border bg-background shadow-lg rounded-lg",
            
            // ANIMATION - fade only, NO transform/zoom (causes iOS caret bugs)
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            // REMOVED: zoom-out-95/zoom-in-95 transforms break iOS Safari input caret positioning
            
            className
          )}
          style={{
            // CRITICAL: Use inline style for max-height on iOS Safari
            // This bypasses CSS variable parsing issues and dvh unit problems
            maxHeight: modalMaxHeight ? `${modalMaxHeight}px` : 'calc(100dvh - 24px)',
            // Ensure no transforms that could break iOS caret
            transform: 'none',
          }}
          // Stop click propagation so clicking inside modal doesn't close it
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {children}
          <DialogPrimitive.Close 
            className={cn(
              "absolute right-3 top-3 z-10",
              "rounded-sm opacity-70 ring-offset-background transition-opacity",
              "hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              "disabled:pointer-events-none",
              // Touch-friendly tap target
              "min-h-[44px] min-w-[44px] flex items-center justify-center"
            )}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
        {MODAL_DEBUG && <ModalDebugOverlay contentRef={internalRef} />}
      </DialogOverlay>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * DialogHeader - Fixed header area (does not scroll)
 * shrink-0 prevents compression when content is tall
 */
const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn(
      "shrink-0",
      "p-4 pb-2",
      "flex flex-col space-y-1.5",
      "text-left",
      "pr-12", // Space for close button
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

/**
 * DialogBody - Scrollable content area
 * 
 * CRITICAL for iOS Safari:
 * - flex-1: Takes available space between header and footer
 * - min-h-0: Required for flex item to shrink below content size (enables scroll)
 * - overflow-y-auto: Enables vertical scrolling
 * - -webkit-overflow-scrolling: touch: Enables momentum scrolling on iOS
 * - overscroll-contain: Prevents scroll chaining to body
 */
const DialogBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // Takes available space, allows shrinking for scroll
      "flex-1 min-h-0",
      // Scrolling
      "overflow-y-auto overflow-x-hidden",
      "overscroll-contain",
      // Padding
      "p-4 pt-2",
      className
    )}
    style={{ 
      // iOS Safari momentum scrolling
      WebkitOverflowScrolling: 'touch',
    }}
    {...props}
  />
))
DialogBody.displayName = "DialogBody"

/**
 * DialogFooter - Fixed footer area (does not scroll)
 * shrink-0 prevents compression
 * Includes safe-area-inset-bottom for iOS home indicator
 */
const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn(
      "shrink-0",
      "p-4 pt-2",
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className
    )}
    style={{
      // Extra padding for iOS home indicator
      paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1rem)',
    }}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

/**
 * DialogTitle - Modal title
 */
const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

/**
 * DialogDescription - Optional description below title
 */
const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
